"use client";

import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import Image from "next/image";
import Link from "next/link";
import { useOptimizedImage } from '@/hooks/useOptimizedImage';
import { getRecommendedImageProps, getContextDimensions } from '@/utils/imageOptimization';
import { IMAGE_CONTEXTS } from '@/constants/imageConfig';
import styles from "./RichTextRenderer.module.css";

type RichTextRendererProps = {
  document: any;
};

// Optimized image component for blog content
const RichTextImageComponent = ({ asset }: { asset: any }) => {
  const title = asset.title || "";
  const description = asset.description || "";
  const originalWidth = asset.width || 800;
  const originalHeight = asset.height || 600;
  const url = asset.url;

  const optimizedImageProps = useOptimizedImage(
    url,
    description || title || "Blog image",
    originalWidth,
    originalHeight,
    IMAGE_CONTEXTS.BLOG_CONTENT,
    false // Blog content images are typically below the fold
  );

  if (!optimizedImageProps) {
    return (
      <div className={styles.assetContainer}>
        <p>Image URL not found</p>
      </div>
    );
  }

  // Get recommended Next.js props for blog content
  const recommendedProps = getRecommendedImageProps(IMAGE_CONTEXTS.BLOG_CONTENT);
  const contextDimensions = getContextDimensions(IMAGE_CONTEXTS.BLOG_CONTENT);

  return (
    <div className={styles.assetContainer}>
      <div className={styles.assetWrapper}>
        <Image
          // Use optimized props for non-conflicting features
          src={optimizedImageProps.src}
          alt={optimizedImageProps.alt}
          priority={optimizedImageProps.priority}
          placeholder={optimizedImageProps.placeholder}
          blurDataURL={optimizedImageProps.blurDataURL}
          // Use recommended Next.js props for optimization
          width={contextDimensions.width}
          height={contextDimensions.height}
          sizes={recommendedProps.sizes}
          quality={recommendedProps.quality}
          className={styles.image}
        />
        {title && (
          <p className={styles.imageCaption}>{title}</p>
        )}
      </div>
    </div>
  );
};

export default function RichTextRenderer({ document }: RichTextRendererProps) {
  // Handle both old structure (direct json) and new structure (with json and links)
  let richTextJson;
  let links;
  
  if (document.json && document.links) {
    // New structure: { json: {...}, links: {...} }
    richTextJson = document.json;
    links = document.links;
  } else {
    // Old structure: direct json document
    richTextJson = document;
    links = document.links;
  }
  
  // Create asset map from the links
  const assetMap = new Map();
  
  if (links && links.assets && links.assets.block) {
    links.assets.block.forEach((asset: any) => {
      assetMap.set(asset.sys.id, asset);
    });
  } else {
    // console.log("No assets found in links structure"); // Removed console.log
    // console.log("links:", links); // Removed console.log
  }

const options = {
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text: React.ReactNode) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text: React.ReactNode) => <u>{text}</u>,
      [MARKS.CODE]: (text: React.ReactNode) => (
        <code className={styles.inlineCode}>{text}</code>
      ),
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => (
        <p className={styles.paragraph}>{children}</p>
      ),
      [BLOCKS.HEADING_1]: (node: any, children: React.ReactNode) => (
        <h1 className={styles.heading1}>{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => (
        <h2 className={styles.heading2}>{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => (
        <h3 className={styles.heading3}>{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node: any, children: React.ReactNode) => (
        <h4 className={styles.heading4}>{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node: any, children: React.ReactNode) => (
        <h5 className={styles.heading5}>{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node: any, children: React.ReactNode) => (
        <h6 className={styles.heading6}>{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => (
        <ul className={styles.unorderedList}>{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => (
        <ol className={styles.orderedList}>{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node: any, children: React.ReactNode) => (
        <li className={styles.listItem}>{children}</li>
      ),
      [BLOCKS.QUOTE]: (node: any, children: React.ReactNode) => (
        <blockquote className={styles.blockquote}>
          {children}
        </blockquote>
      ),
      [BLOCKS.HR]: () => <hr className={styles.hr} />,
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        try {
          // Get the asset ID from the node
          const assetId = node.data.target.sys.id;
          
          // console.log("Looking for asset with ID:", assetId); // Removed console.log
          // console.log("Available assets:", Array.from(assetMap.keys())); // Removed console.log
          
          // Find the asset in our asset map
          const asset = assetMap.get(assetId);
          
          if (!asset) {
            // console.error("Asset not found in asset map:", assetId); // Removed console.log
            return (
              <div className={styles.assetContainer}>
                <p>Image could not be loaded (Asset not found)</p>
              </div>
            );
          }
          
          // console.log("Found asset:", JSON.stringify(asset, null, 2)); // Removed console.log
          
          // Extract image information
          const url = asset.url;
          
          if (!url) {
            // console.error("No URL found in asset"); // Removed console.log
            return (
              <div className={styles.assetContainer}>
                <p>Image URL not found</p>
              </div>
            );
          }

          // Use the optimized image component
          return <RichTextImageComponent asset={asset} />;
        } catch (error) {
          // console.error("Error rendering embedded asset:", error); // Removed console.log
          // console.error("Node data:", JSON.stringify(node, null, 2)); // Removed console.log
          
          return (
            <div className={styles.assetContainer}>
              <div className={styles.assetWrapper}>
                <p>Error loading image</p>
              </div>
            </div>
          );
        }
      },
      [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
        const { uri } = node.data;
        const isExternal = uri.startsWith("http");

        return isExternal ? (
          <a
            href={uri}
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ) : (
          <Link href={uri} className={styles.link}>
            {children}
          </Link>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, children: React.ReactNode) => {
        const { slug } = node.data.target.fields;
        return (
          <Link href={`/blog/${slug}`} className={styles.link}>
            {children}
          </Link>
        );
      },
    },
  };

  return <>{documentToReactComponents(richTextJson, options)}</>;
} 